import {
  CompanyInfo,
  CompanySize,
  PersonalInfo,
  SubscriptionRequest,
  SubscriptionResponse,
} from '@upright/common/activities/contact-forms/company-subscription/types';
import { createContext, useContext, useEffect, useState } from 'react';
import { UseMutationResult } from '@tanstack/react-query';
import { SubscriptionTier } from '../../../../../common/activities/contact-forms/company-subscription/subscription-tiers';
import { useQueryParams } from '../../../generic/hooks/url-handling/query-params';
import { useSubmitSubsriptionForm, useValidatePromoCode } from './query-hooks';
import { subscriptionModalParamKeys, SubscriptionModalQueryParams } from './types';
import { validateCompanyInfo, validatePersonalInfo, validatePromoCode, validateTermsAccepted } from './validation';

type ValueAndSetter<T> = [Partial<T>, (newValue: Partial<T>) => void];

interface SubscriptionContext {
  params: ValueAndSetter<Partial<SubscriptionModalQueryParams>>;
  personalInfo: ValueAndSetter<PersonalInfo>;
  companyInfo: ValueAndSetter<CompanyInfo>;
  promoCode: ValueAndSetter<string | undefined>;
  termsAccepted: ValueAndSetter<boolean>;
  message: ValueAndSetter<string | undefined>;
  password: ValueAndSetter<string | undefined>;
  submitButtonClicked: [boolean, (value: boolean) => void];
  submitQuery: UseMutationResult<SubscriptionResponse, Error, SubscriptionRequest>;
  modalOpen: ValueAndSetter<boolean>;
  validatePromoCodeQuery: UseMutationResult<{ code: string }, Error, { code: string }>;
  tier?: SubscriptionTier;
  setTier: (newTier: SubscriptionTier) => void;
  submit: () => void;
  canSubmit: boolean;
  reset: () => void;
}

const mockPersonalInfo: PersonalInfo = {
  firstName: 'jjjj',
  lastName: 'jjjsdfsdfasdf',
  email: 'oula@testaa.com',
};

const mockCompanyInfo: CompanyInfo = {
  name: 'Firima',
  size: CompanySize._10,
};

export function useSubscriptionContextBuilder(): SubscriptionContext {
  const [queryParams, setQueryParams] = useQueryParams<SubscriptionModalQueryParams>(subscriptionModalParamKeys);
  const [modalOpen, setModalOpen] = [
    !!queryParams.subscribe,
    (open: boolean) => {
      setQueryParams({ subscribe: open ? 'true' : undefined }, 'push');
    },
  ];

  const devMode = false as boolean;
  const initialPersonalInfo = devMode ? mockPersonalInfo : {};
  const initialCompanyInfo = devMode ? mockCompanyInfo : {};
  const [personalInfo, setPersonalInfo] = useState<Partial<PersonalInfo>>(initialPersonalInfo);
  const [companyInfo, setCompanyInfo] = useState<Partial<CompanyInfo>>(initialCompanyInfo);
  const [promoCode, setPromoCode] = useState<string | undefined>(queryParams.code);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [message, setMessage] = useState<string>();
  const [password, setPassword] = useState<string>();
  const submitButtonClicked = useState(false);

  function onSuccess() {
    setQueryParams({ success: 'true' }, 'push');
  }
  const submitQuery = useSubmitSubsriptionForm(onSuccess);
  const validatePromoCodeQuery = useValidatePromoCode();
  const requestCodeValidation = validatePromoCodeQuery.mutate;

  useEffect(() => {
    if (queryParams.code) {
      setPromoCode(queryParams.code);
      requestCodeValidation({ code: queryParams.code });
    }
  }, [queryParams.code, requestCodeValidation]);

  useEffect(() => {
    if (queryParams.companyName) {
      setCompanyInfo(curr => ({ ...curr, name: queryParams.companyName }));
    }
  }, [queryParams.companyName]);

  function reset() {
    setPersonalInfo({});
    setCompanyInfo({});
    setPromoCode(undefined);
    setTermsAccepted(false);
    setMessage(undefined);
    submitQuery.reset();
    submitButtonClicked[1](false);
    validatePromoCodeQuery.reset();
  }

  const { tier } = queryParams;
  const canSubmit =
    !!tier &&
    validateCompanyInfo(companyInfo, tier) &&
    validatePersonalInfo(personalInfo) &&
    validatePromoCode(promoCode, validatePromoCodeQuery) &&
    validateTermsAccepted(termsAccepted, tier);

  function submit() {
    if (canSubmit) {
      submitQuery.mutate({
        tier,
        personalInfo,
        companyInfo: { ...companyInfo, companyId: queryParams.companyId },
        promoCode,
        message,
        password,
      });
    }
  }

  return {
    params: [
      queryParams,
      (params: Partial<SubscriptionModalQueryParams>) => {
        setQueryParams(params, 'push');
      },
    ],
    personalInfo: [personalInfo, setPersonalInfo],
    companyInfo: [companyInfo, setCompanyInfo],
    promoCode: [promoCode, setPromoCode],
    termsAccepted: [termsAccepted, setTermsAccepted],
    message: [message, setMessage],
    password: [password, setPassword],
    submitButtonClicked,
    submitQuery,
    validatePromoCodeQuery,
    setTier: (t: SubscriptionTier) => {
      setQueryParams({ tier: t }, 'push');
    },
    tier: queryParams.tier,
    submit,
    canSubmit,
    reset,
    modalOpen: [modalOpen, setModalOpen],
  };
}

const ctx = createContext({} as SubscriptionContext);

export function useSubscriptionContext() {
  return useContext(ctx);
}

export const SubscriptionContextProvider = ctx.Provider;
